import { Controller } from '@hotwired/stimulus'
import { FetchRequest } from '@rails/request.js'
import ReactRailsUJS from 'react_ujs'
import { debounce } from 'underscore'
import { performWithCsrfRetry } from '../../../helpers/csrf'
import { disable, enable } from '../../../helpers/custom_events_helper'

export default class Autosave extends Controller {
  static values = {
    url: String,
    finalizeUrl: String,
    debounceTime: { type: Number, default: 900 },
    jitterProtectionTime: { type: Number, default: 3000 }
  }

  connect() {
    this.jitterProtectedElements = new Array()
    this.fieldsPendingUpdate = new Set()

    // Create a debounced save function
    this.debouncedSave = debounce(this.saveChanges, this.debounceTimeValue)

    this.element.addEventListener('change', this.inputChanged)
    this.element.addEventListener('turbo:submit-start', this.onSubmitStart);
    this.element.addEventListener('turbo:submit-end',   this.onSubmitEnd);

    document.addEventListener('turbo:before-morph-element', this.preventInputElementJitter)
    document.addEventListener('turbo:before-morph-element', this.ignoreReactComponents)
    document.addEventListener('turbo:before-render', this.unmmountReactComponents)
    window.addEventListener('pageshow', this.pageshowHandler)


    console.log('SuperForm: auto-save enabled.')
  }

  disconnect() {
    this.element.removeEventListener('change', this.inputChanged)
    this.element.removeEventListener('turbo:submit-start', this.onSubmitStart);
    this.element.removeEventListener('turbo:submit-end',   this.onSubmitEnd);

    document.removeEventListener('turbo:before-morph-element', this.preventInputElementJitter)
    document.removeEventListener('turbo:before-morph-element', this.ignoreReactComponents)
    document.removeEventListener('turbo:before-render', this.unmmountReactComponents)
    window.removeEventListener('pageshow', this.pageshowHandler)
  }

  pageshowHandler = (e) => {
    if (e.persisted) Turbo.visit(location.href, { action: 'replace' })
  }

  inputChanged = event => {
    let target = event.target
    let elementName = target.name
    this.fieldsPendingUpdate.add(elementName)

    this.jitterProtectedElements.push(elementName)
    // Pop the oldest input name off the jitterProtectedElements array after 5x the debounce time.
    setTimeout(() => {this.jitterProtectedElements.shift() }, this.jitterProtectionTimeValue)

    // Remove error classes from elements because jitter protection will prevent them from updating.
    this.element.querySelectorAll(`[name="${elementName}"]`).forEach(el => el.classList.remove('is-invalid'))

    this.debouncedSave()
  }

  saveChanges = () => {
    // Copy the changed fields to a new array to prevent mutation during iteration.
    const updateBatch = Array.from(this.fieldsPendingUpdate)
    const updateBatchSize = updateBatch.length

    if (updateBatchSize === 0) {
      document.dispatchEvent(new CustomEvent('super-form:autosave:completed', { detail: { updateBatchSize: updateBatchSize } }))
      return
    }

    // Clear the changed fields tracking object
    this.fieldsPendingUpdate = new Set()

    const data = new FormData()

    // Process all tracked elements and add them to the form data
    updateBatch.forEach(name => {
      if(name.match(/\[\]\[/)) {
        // Handle BasicList fields
        const fieldset = this.element.querySelector(`[name="${name}"]`).closest('fieldset')
        const elements = fieldset.querySelectorAll('input, textarea')
        this.appendToFormData(elements, data)
      } else {
        const elements = this.element.querySelectorAll(`[name="${name}"]`)
        this.appendToFormData(elements, data)
      }
    })

    let request

    document.dispatchEvent(new CustomEvent('super-form:autosave:started', { detail: { request: request } }))
    performWithCsrfRetry('patch', this.urlValue, {
      body: data,
      responseKind: 'json'
    }).then(({ request: req, response }) => {
      request = req

      if (response.ok) {
        document.dispatchEvent(new CustomEvent('super-form:autosave:success', { detail: { response: response } }))
      } else if (response.statusCode === 401) {
        // If still unauthorized after Csrf retry, reload page to redirect to proper URL
        window.location.reload()
      } else {
        console.error('SuperForm: autosave failed', response)
        document.dispatchEvent(new CustomEvent('super-form:autosave:failed', { detail: { response } }))
      }
    }).catch(error => {
      console.error('SuperForm: autosave failed', error)
      document.dispatchEvent(new CustomEvent('super-form:autosave:failed', { detail: { error: error } }))
    }).finally(() => {
      document.dispatchEvent(new CustomEvent('super-form:autosave:completed', { detail: { request: request, updateBatchSize: updateBatchSize } }))
    })
  }

  appendToFormData = (elements, formData) => {
    Array.from(elements).forEach(el => {
      if (el.type === 'checkbox' || el.type === 'radio') {
        if (el.checked) {
          formData.append(el.name, el.value)
        }
      } else {
        formData.append(el.name, el.value)
      }
    })
  }

  // Prevents jitter when an input element is morphed by Turbo.
  preventInputElementJitter = event => {
    const detail = event.detail
    const newEl = detail.newElement
    if (!newEl?.name) return

    const target = event.target

    // prevent turbo-morph from replacing protected inputs, but ensure that new classes
    // are transfered from the new element

    function applyNewElementClasses() {
      const existing = document.querySelectorAll(`[name='${newEl.name}']`)
      existing.forEach((el) => {
        el.className = newEl.className
      })
    }

    if (this.jitterProtectedElements.includes(newEl.name)) {
      event.preventDefault()
      applyNewElementClasses()
      console.log(`SuperForm#preventInputElementJitter: prevented jitter for recently updated element ${newEl.name}`)
    } else if(!target.readOnly && target.name === document.activeElement.name) {
      event.preventDefault()
      applyNewElementClasses()
      console.log(`SuperForm#preventInputElementJitter: prevented jitter for active element ${newEl.name}`)
    }
  }

  ignoreReactComponents = event => {
    const target = event.target

    if (target.dataset['reactClass']) {
      event.preventDefault()
    }
  }

  finalize = async (event) => {
    if (this.debouncedSave?.cancel) this.debouncedSave.cancel();
    await this.saveChanges();
    this.element.action = this.finalizeUrlValue;
    this.element.requestSubmit(event?.currentTarget);
  }

  // Disable ALL submit controls at the start of ANY submit (Finish or Save & Exit)
  onSubmitStart = (e) => {
    const form = e.target;
    const submitters = form.querySelectorAll('button[type="submit"], input[type="submit"]');

    submitters.forEach(btn => {
      disable(btn)
      btn.disabled = true;
    });
  };

  // Restore submit controls after the submit completes (success or 422)
  onSubmitEnd = (e) => {
    const form = e.target;
    const submitters = form.querySelectorAll('button[type="submit"], input[type="submit"]');

    submitters.forEach(btn => {
      enable(btn)
      btn.disabled = false;
    });
  };

  unmmountReactComponents = () => ReactRailsUJS.unmountComponents()
}

export { Autosave }
